"use client";
import { type Context, createContext } from "react";

import type {
  IOspApiClientContext,
  IOspApiClientProviderProps,
} from "../types";
import { useCreateOspApi } from "@hooks/useCreateOspApi";
import { useSyncOwnerLocaleOnLogin } from "@hooks/useSyncOwnerLocale";
import { setSubscribeEmailStatusResolver } from "@components/SubscribeEmailPrompt/subscribeEmailPromptGate";
import { createSubscribeEmailStatusResolver } from "@components/SubscribeEmailPrompt/subscribeEmailStatusResolver";
import {
  createSubscribeEmailSubmitter,
  setSubscribeEmailSubmitter,
} from "@components/SubscribeEmailPrompt/subscribeEmailSubmitter";

export const OspApiClientContext: Context<IOspApiClientContext> =
  createContext<IOspApiClientContext>({});

export const OspApiClientProvider = ({
  children,
}: IOspApiClientProviderProps) => {
  const {
    openApiClient,
    questApiClient,
    chatApiClient,
    aiApiClient,
    notifyApiClient,
    swapApiClient,
    openDataApiClient,
    memberApiClient,
    trackerApiClient,
    accountReadsApi,
    financialSyncStatusApi,
  } = useCreateOspApi();

  // 登录后把本机语言（设备语言 / 用户切过的语言）对账到后端 owner.locale。
  // 挂在这里是因为本 Provider 是「api client + 登录态」都齐活的最外层，且全站唯一挂载一次。
  useSyncOwnerLocaleOnLogin(memberApiClient);

  // 注册「订阅邮箱提示」默认状态 resolver（幂等；查询在弹窗判定时才触发）
  setSubscribeEmailStatusResolver(
    createSubscribeEmailStatusResolver({
      openApi: openApiClient,
      memberApi: memberApiClient,
    }),
  );

  // 注册订阅提交器（POST /agents/preferences，点击 CTA 时才触发）
  setSubscribeEmailSubmitter(createSubscribeEmailSubmitter(memberApiClient));

  return (
    <OspApiClientContext.Provider
      value={{
        openApi: openApiClient,
        aiApi: aiApiClient,
        questApi: questApiClient,
        chatApi: chatApiClient,
        notifyApi: notifyApiClient,
        swapApi: swapApiClient,
        openDataApi: openDataApiClient,
        memberApi: memberApiClient,
        trackerApi: trackerApiClient,
        accountReadsApi,
        financialSyncStatusApi,
      }}
    >
      {children}
    </OspApiClientContext.Provider>
  );
};
